import {AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {A11yModule} from '@angular/cdk/a11y';
import {ApplicationPipesModule} from '../../pipes/application-pipes.module';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {MonitoradorPedidos} from '../../fidelidade/MonitoradorPedidos';
import {ServidorImpressaoExecutorService} from '../../services/servidor-impressao-executor.service';
import {FuncaoMenuV2, GRUPOS_MENU_V2, GrupoMenuV2} from './menu-v2.catalogo';
import {criarCatalogoParaUsuario} from './menu-v2.contexto';
import {UsoMenuV2} from './menu-v2.uso';
import {MenuBuscaComponent} from './menu-busca.component';
import {ExtensaoWhatsapp, extensaoDoNumero} from '../../numero-whatsapp/extensoes-whatsapp';

export type EstadoLoja = 'aberta' | 'pausada' | 'fechada';
export interface ResumoLoja { estado: EstadoLoja; titulo: string; detalhe: string; }
export interface ServicoHoje { nome: string; aberto: boolean; horario: string; detalhe: string; }
export interface IntegracaoOperacao { tipo: 'ifood' | 'keeta' | '99food'; loja: any; abrir: boolean; }
export interface StatusIntegracao {
  tipo: 'ifood' | 'keeta' | '99food'; rotulo: string; nome: string; loja: any;
  aberta: boolean; desativado: boolean; processando: boolean; mensagem: string;
  /** iFood só é consultado; Keeta e 99Food aceitam abrir/fechar daqui. */
  controlavel: boolean;
}

@Component({
  selector: 'app-menu-v2',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, A11yModule, ApplicationPipesModule, MenuBuscaComponent],
  templateUrl: './menu-v2.component.html',
  styleUrls: ['./menu-v2.component.scss']
})
export class MenuV2Component implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() usuario: any;
  @Input() lojaAberta = false;
  @Output() onAbrirLoja = new EventEmitter<void>();
  @Output() onFecharLoja = new EventEmitter<void>();
  @Output() onGerenciarJobs = new EventEmitter<void>();
  @Output() onSair = new EventEmitter<void>();
  @Output() onLayoutChange = new EventEmitter<'topo' | 'lateral'>();
  @Output() onRecarregar = new EventEmitter<void>();
  @Output() onMenuLegado = new EventEmitter<void>();
  /** Abrir ou fechar a loja em um parceiro que aceita controle pelo painel (Keeta e 99Food). */
  @Output() onIntegracao = new EventEmitter<IntegracaoOperacao>();
  layout: 'topo' | 'lateral' = 'lateral';
  @ViewChild(MenuBuscaComponent) buscaFuncoes?: MenuBuscaComponent;
  empresa: any = {};
  funcoes: FuncaoMenuV2[] = [];
  grupos = GRUPOS_MENU_V2;
  secoes: {nome: string; itens: FuncaoMenuV2[]}[] = [];
  grupoAberto: GrupoMenuV2 | null = null;
  grupoLateralAberto: GrupoMenuV2 | null = null;
  secoesLaterais: {nome: string; itens: FuncaoMenuV2[]}[] = [];
  grupoAtivo: GrupoMenuV2 | null = null;
  pendenciasAbertas = false;
  operacaoAberta = false;
  contaAberta = false;
  mobileAberto = false;
  rotaAtual = '';
  erroNavegacao = '';
  novasMesas = 0;
  alertasEstoque = 0;
  readonly atalho = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ K' : 'Ctrl K';
  /** Uso por navegador: só reordena busca e acessos rápidos, nunca o menu. */
  readonly uso = new UsoMenuV2(UsoMenuV2.armazenamentoLocal());
  private destruir$ = new Subject<void>();
  private focoPainel: HTMLElement | null = null;
  private funcaoAtivaId: string | null = null;
  private rolagemLateralPendente = true;

  constructor(private constants: ConstantsService, private router: Router,
              private host: ElementRef<HTMLElement>, private monitor: MonitoradorPedidos,
              private servidorImpressao: ServidorImpressaoExecutorService) {}

  ngOnInit(): void {
    try { this.layout = localStorage.getItem('meucardapio-menu-v2-layout') === 'topo' ? 'topo' : 'lateral'; } catch (_) {}
    Promise.resolve().then(() => this.onLayoutChange.emit(this.layout));
    this.constants.empresa$.pipe(takeUntil(this.destruir$)).subscribe(empresa => {
      this.empresa = empresa || {};
      this.atualizarCatalogo();
      if (empresa?.id && empresa.exibirWizard && !this.router.url.includes('/wizard')) {
        this.router.navigateByUrl('/admin/wizard');
      }
    });
    this.router.events.pipe(takeUntil(this.destruir$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.fecharPaineis();
        this.atualizarRota();
      }
    });
    this.monitor.resumoMesas$.pipe(takeUntil(this.destruir$)).subscribe(resumo => this.novasMesas = Number(resumo?.totalNovo) || 0);
    this.monitor.resumoEstoque$.pipe(takeUntil(this.destruir$)).subscribe(resumo => this.alertasEstoque = Number(resumo?.totalAlertas) || 0);
  }

  ngOnChanges(): void { this.atualizarCatalogo(); }

  ngAfterViewChecked(): void {
    if (!this.rolagemLateralPendente || this.layout !== 'lateral') return;
    this.rolagemLateralPendente = false;
    const menu = this.host.nativeElement.querySelector<HTMLElement>('.mv2-nav');
    const item = menu?.querySelector<HTMLElement>('.mv2-inline .selecionado')
      || menu?.querySelector<HTMLElement>('.mv2-nav-item.ativo');
    if (!menu?.clientHeight || !item?.getClientRects().length) return;
    const area = menu.getBoundingClientRect();
    const botao = item.getBoundingClientRect();
    const topo = area.top + menu.clientTop;
    const fundo = topo + menu.clientHeight;
    if (botao.top < topo) menu.scrollTop += botao.top - topo;
    else if (botao.bottom > fundo) menu.scrollTop += botao.bottom - fundo;
  }

  ngOnDestroy(): void {
    this.destruir$.next();
    this.destruir$.complete();
  }

  private atualizarCatalogo(): void {
    this.funcoes = criarCatalogoParaUsuario(this.usuario, this.empresa, this.constants, this.servidorImpressao.ehUmServidorAtivo());
    this.grupos = GRUPOS_MENU_V2.filter(g => this.funcoes.some(f => f.grupo === g.id && !f.somenteBusca));
    this.atualizarRota();
    if (this.grupoAberto) this.prepararSecoes(this.grupoAberto);
    this.prepararSecoesLaterais();
  }

  private destinoAtivo(item: FuncaoMenuV2): string {
    if (!item.rota || item.externa) return '';
    const destino = this.router.parseUrl(item.rota);
    // O resolver do WhatsApp leva quem tem acesso ativo do paywall à configuração.
    if (item.id === 'cloud-whatsapp/paywall' && this.rotaAtual === '/admin/cloud-whatsapp/config') {
      return '/admin/cloud-whatsapp/config';
    }
    // As telas internas de campanha não ficam abaixo da rota /marketing.
    if (item.id === 'marketing' && /^\/admin\/(nova-campanha|nova-campanha-template|tela-enviar-mensagens|campanha)(\/|$)/.test(this.rotaAtual)) {
      return this.rotaAtual;
    }
    return destino.queryParams.redirectTo ? '/admin/' + destino.queryParams.redirectTo : item.rota.split('?')[0];
  }

  private atualizarRota(): void {
    const funcaoAnterior = this.funcaoAtivaId;
    const rotaAnterior = this.rotaAtual, grupoAnterior = this.grupoAtivo;
    this.rotaAtual = this.router.url.split('?')[0].split('#')[0];
    const correspondencias = this.funcoes.filter(f => {
      const destino = this.destinoAtivo(f);
      return destino && (this.rotaAtual === destino || this.rotaAtual.startsWith(destino + '/'));
    }).sort((a, b) => this.destinoAtivo(b).length - this.destinoAtivo(a).length);
    const funcao = correspondencias[0];
    this.funcaoAtivaId = correspondencias.find(f => !f.somenteBusca)?.id || null;
    this.grupoAtivo = funcao?.grupo || null;
    if (this.rotaAtual !== rotaAnterior || this.funcaoAtivaId !== funcaoAnterior) this.rolagemLateralPendente = true;
    if (this.rotaAtual !== rotaAnterior || this.grupoAtivo !== grupoAnterior) {
      this.grupoLateralAberto = ['inicio', 'pay'].includes(this.grupoAtivo) ? null : this.grupoAtivo;
      this.prepararSecoesLaterais();
    }
  }

  itemAtivo(item: FuncaoMenuV2): boolean {
    return this.funcaoAtivaId === item.id;
  }
  get iniciais(): string { return (this.usuario?.nome || 'Minha conta').trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase(); }
  /** Mesmo número que o menu antigo exibia: o de atendimento, com o da loja como reserva. */
  get whatsappPrincipal(): string { return this.empresa?.numeroWhatsapp?.whatsapp || this.empresa?.whatsapp || ''; }

  /** O menu antigo mostrava o enum cru ("CloudWhatsapp"); aqui vira rótulo legível. */
  get meioDeEnvio(): {nome: string; icone: string; alerta: boolean} | null {
    const meio = String(this.empresa?.meioDeEnvio || this.usuario?.empresa?.meioDeEnvio || '');
    if (!meio) return null;
    if (meio === 'CloudWhatsapp') return {nome: 'Cloud WhatsApp', icone: 'fe-cloud', alerta: false};
    if (meio === 'Whatsapp') return {nome: 'WhatsApp', icone: 'fe-smartphone', alerta: false};
    if (meio.startsWith('SMS')) return {nome: 'SMS', icone: 'fe-message-square', alerta: false};
    if (meio === 'Mock') return {nome: 'Envio simulado', icone: 'fe-alert-triangle', alerta: true};
    return {nome: meio, icone: 'fe-send', alerta: false};
  }
  /** Envio pelo WhatsApp Web: o selo vira link direto para a extensão que o número principal exige. */
  get extensaoWhatsapp(): ExtensaoWhatsapp | null {
    const meio = String(this.empresa?.meioDeEnvio || this.usuario?.empresa?.meioDeEnvio || '');
    return meio === 'Whatsapp' ? extensaoDoNumero(this.empresa?.numeroWhatsapp) : null;
  }
  get inicio(): FuncaoMenuV2 | undefined { return this.funcoes.find(f => f.grupo === 'inicio') || this.funcoes.find(f => !!f.rota && !f.externa); }
  get funcoesConta(): FuncaoMenuV2[] { return this.funcoes.filter(f => ['loja-publica', 'sair'].includes(f.id)); }
  get cardapioPublico(): FuncaoMenuV2 | undefined { return this.funcoes.find(f => f.id === 'loja-publica'); }
  /** Mesma função do catálogo: só existe para quem já vê "Números WhatsApp" no menu. */
  get numerosWhatsapp(): FuncaoMenuV2 | undefined { return this.funcoes.find(f => f.id === 'loja/numeros-whatsapp'); }

  abrirNumerosWhatsapp(evento: Event): void {
    const item = this.numerosWhatsapp;
    if (!item) return;
    evento.stopPropagation();
    this.executar(item);
  }
  get statusLoja(): string { return this.resumoLoja.titulo; }

  /** Substituível nos testes. */
  agora(): Date { return new Date(); }

  /** Estado único da loja: o cabeçalho mostra o título e o painel de operação acrescenta o detalhe. */
  get resumoLoja(): ResumoLoja {
    const servico = this.servicoPrincipal;
    if (this.empresa.fechadoTemporariamente) {
      return {estado: 'pausada', titulo: 'Pedidos pausados',
        detalhe: this.empresa.mensagemPausaProgramada || 'Pausada manualmente. Reabra para voltar a receber pedidos.'};
    }
    if (this.lojaAberta) {
      const fechamento = this.horarioFechamento(servico);
      return {estado: 'aberta', titulo: 'Recebendo pedidos', detalhe: fechamento ? 'Fecha às ' + fechamento : 'Recebendo pedidos fora do horário'};
    }
    return {estado: 'fechada', titulo: 'Loja fechada', detalhe: servico?.mensagemAbrirPedidos || servico?.motivo || 'Fora do horário de funcionamento'};
  }

  /** Mesmo serviço que o shell usa para decidir `lojaAberta`. */
  private get servicoPrincipal(): any { return (this.empresa.horariosDoServico || [])[0]; }

  /** Horário "HH:MM" em que o turno em andamento termina, ou null fora dos turnos de hoje. */
  horarioFechamento(servico: any): string | null {
    const agora = this.agora();
    const minutosAgora = agora.getHours() * 60 + agora.getMinutes();
    const minutos = (hora: string) => Number(hora.substr(0, 2)) * 60 + Number(hora.substr(3, 2));
    for (const turno of (servico?.horariosHoje || [])) {
      if (!turno.funciona || !turno.horarioAbertura || !turno.horarioFechamento) continue;
      const abre = minutos(turno.horarioAbertura);
      let fecha = minutos(turno.horarioFechamento);
      if (fecha <= abre) fecha += 24 * 60; // vira a madrugada
      if (minutosAgora >= abre && minutosAgora < fecha) return turno.horarioFechamento.substr(0, 5);
    }
    return null;
  }

  get servicosHoje(): ServicoHoje[] {
    return (this.empresa.horariosDoServico || []).map((servico: any) => {
      const aberto = !!(servico.estaAberta && servico.estaRecebendoPedidos) && !this.empresa.fechadoTemporariamente;
      const fechamento = aberto ? this.horarioFechamento(servico) : null;
      return {nome: servico.label || servico.servico, aberto, horario: servico.descricaoHorarioSemDia || servico.descricaoHorario || 'Fechado',
        detalhe: aberto ? (fechamento ? 'Fecha às ' + fechamento : 'Aberto') : (servico.mensagemAbrirPedidos || servico.motivo || 'Fechado')};
    });
  }

  /** Lojas dos parceiros que a empresa configurou; o status vem do monitorador, como no menu antigo. */
  get integracoes(): StatusIntegracao[] {
    const lista: StatusIntegracao[] = [];
    const montar = (tipo: StatusIntegracao['tipo'], rotulo: string, lojas: any[], controlavel: boolean) =>
      (lojas || []).forEach(loja => lista.push({tipo, rotulo, nome: loja.loja || loja.id || rotulo, loja, aberta: !!loja.aberta,
        desativado: !!loja.desativado, processando: !!loja.processando, mensagem: loja.mensagem || '', controlavel: controlavel && !loja.desativado && !loja.desconectada}));
    const ifoodAtivo = (this.empresa.integracoesIfood || []).some((i: any) => !i.desativado && (i.orderApi || i.shippingApi));
    if (ifoodAtivo) montar('ifood', 'iFood', this.monitor.lojasIfood, false);
    if (this.empresa.integracoesKeeta?.length) montar('keeta', 'Keeta', this.monitor.lojasKeeta, true);
    if (this.empresa.integracoes99Food?.length) montar('99food', '99Food', this.monitor.lojas99Food, true);
    return lista;
  }

  alternarIntegracao(integracao: StatusIntegracao): void {
    if (integracao.tipo === 'ifood') { this.monitor.autalizeStatusIfood(); return; }
    if (!integracao.controlavel || integracao.processando) return;
    this.onIntegracao.emit({tipo: integracao.tipo, loja: integracao.loja, abrir: !integracao.aberta});
  }

  /** Abrir/fechar a loja respeita a permissão do catálogo (só gestores com loja). */
  get estadoLoja(): FuncaoMenuV2 | undefined { return this.funcoes.find(f => f.id === 'estado-loja'); }
  get filaImpressao(): FuncaoMenuV2 | undefined { return this.funcoes.find(f => f.id === 'fila-impressao'); }

  abrirPendencias(): void {
    this.fecharPaineis();
    this.pendenciasAbertas = true;
  }
  alertaItem(item: FuncaoMenuV2): number {
    if (item.id === 'pedidos') return this.monitor.novosPedidos.length;
    if (item.id === 'pedidos-mesas') return this.novasMesas;
    if (item.id === 'estoque/alertas') return this.alertasEstoque;
    return item.id === 'pendencias' ? this.pendencias : 0;
  }
  alertaGrupo(grupo: GrupoMenuV2): number {
    return this.funcoes.filter(f => f.grupo === grupo && !f.somenteBusca)
      .reduce((total, item) => total + this.alertaItem(item), 0);
  }

  quantidadePendente(item: FuncaoMenuV2): number {
    const resumo = this.monitor.resumoPedidos.value || {};
    const campos: {[id: string]: string} = {'pedidos/erros': 'totalErroParceiro', 'pedidos/onlineaguardando': 'totalPendenteOnline',
      'pedidos/contestados': 'totalContestados', 'pedidos/errosimportar': 'totalErrosImportar'};
    if (item.id === 'pedidos/alteracaoendereco') return this.monitor.pedidosEnderecoAlterado.length;
    if (item.id === 'pedidos-mesas/erros') return Number(this.monitor.mesasComErros.value?.totalErroParceiroMesa) || 0;
    return Number(resumo[campos[item.id]]) || 0;
  }
  get pendencias(): number { return this.funcoesPendentes.reduce((total, item) => total + this.quantidadePendente(item), 0); }

  get funcoesPendentes(): FuncaoMenuV2[] { return this.funcoes.filter(item => item.secao === 'Pendências' && item.id !== 'pendencias'); }

  get atalhosAplicativos(): FuncaoMenuV2[] { return this.funcoes.filter(f => ['loja-publica', 'kds'].includes(f.id)); }


  alternarLayout(): void {
    this.layout = this.layout === 'topo' ? 'lateral' : 'topo';
    this.rolagemLateralPendente = true;
    try { localStorage.setItem('meucardapio-menu-v2-layout', this.layout); } catch (_) {}
    this.fecharComFoco();
    this.onLayoutChange.emit(this.layout);
  }

  alternarGrupo(grupo: GrupoMenuV2): void {
    this.atualizarCatalogo();
    if (grupo === 'inicio') { if (this.inicio) this.executar(this.inicio); return; }
    if (grupo === 'pay') {
      const formasPagamento = this.funcoes.find(f => f.id === 'loja/formaspagamento/offline');
      if (formasPagamento) this.executar(formasPagamento);
      return;
    }
    const estaAberto = this.layout === 'lateral' ? this.grupoLateralAberto === grupo : this.grupoAberto === grupo;
    if (grupo === 'clientes' && !estaAberto) {
      const primeiraFuncao = this.funcoes.find(f => f.grupo === grupo && !f.somenteBusca);
      if (primeiraFuncao) { this.executar(primeiraFuncao); return; }
    }
    this.focoPainel = document.activeElement as HTMLElement;
    if (this.layout === 'lateral') {
      this.fecharPaineis(false);
      this.grupoLateralAberto = this.grupoLateralAberto === grupo ? null : grupo;
      this.prepararSecoesLaterais();
      return;
    }
    const abrir = this.grupoAberto !== grupo;
    this.fecharPaineis(false);
    this.grupoAberto = abrir ? grupo : null;
    this.prepararSecoes(grupo);
  }

  private prepararSecoes(grupo: GrupoMenuV2): void {
    const itens = this.funcoes.filter(f => f.grupo === grupo && !f.somenteBusca);
    this.secoes = Array.from(new Set(itens.map(f => f.secao))).map(nome => ({nome, itens: itens.filter(f => f.secao === nome)}));
    if (!itens.length) this.grupoAberto = null;
  }

  private prepararSecoesLaterais(): void {
    this.rolagemLateralPendente = true;
    const itens = this.funcoes.filter(f => f.grupo === this.grupoLateralAberto && !f.somenteBusca);
    this.secoesLaterais = Array.from(new Set(itens.map(f => f.secao))).map(nome => ({nome, itens: itens.filter(f => f.secao === nome)}));
    if (!itens.length) this.grupoLateralAberto = null;
  }

  alternarOperacao(): void {
    if (this.operacaoAberta) { this.fecharComFoco(); return; }
    this.atualizarCatalogo();
    this.focoPainel = document.activeElement as HTMLElement;
    const abrir = !this.operacaoAberta;
    this.fecharPaineis();
    this.operacaoAberta = abrir;
  }

  /** Aberto pelo cartão do rodapé do menu lateral: o painel abre ao lado do cartão, não no topo. */
  contaPeloCartao = false;

  alternarConta(peloCartao = false): void {
    if (this.contaAberta) { this.fecharComFoco(); return; }
    this.focoPainel = document.activeElement as HTMLElement;
    const abrir = !this.contaAberta;
    this.fecharPaineis();
    this.contaPeloCartao = peloCartao;
    this.contaAberta = abrir;
  }

  alternarMobile(): void {
    if (this.mobileAberto || this.grupoAberto) { this.fecharComFoco(); return; }
    this.focoPainel = document.activeElement as HTMLElement;
    const abrir = !this.mobileAberto && !this.grupoAberto;
    this.fecharPaineis();
    this.mobileAberto = abrir;
    this.rolagemLateralPendente = true;
  }

  abrirBusca(): void { this.buscaFuncoes?.abrir(); }

  /** Chamado pelo diálogo antes de abrir: catálogo atualizado e painéis fechados. */
  prepararBusca(): void {
    this.atualizarCatalogo();
    this.fecharPaineis();
  }

  executar(item: FuncaoMenuV2): void {
    // Revalida a lista atual: uma atualização de empresa pode remover a função enquanto a busca está aberta.
    this.atualizarCatalogo();
    item = this.funcoes.find(f => f.id === item.id);
    if (!item) return;
    this.focoPainel = document.activeElement as HTMLElement;
    this.uso.registrar(item.id);
    this.erroNavegacao = '';
    this.buscaFuncoes?.fechar();
    this.fecharPaineis();
    if (item.acao) {
      if (item.acao === 'abrirLoja') this.onAbrirLoja.emit();
      if (item.acao === 'fecharLoja') this.onFecharLoja.emit();
      if (item.acao === 'impressao') this.onGerenciarJobs.emit();
      if (item.acao === 'sair') this.onSair.emit();
      if (item.acao === 'pendencias') this.pendenciasAbertas = true;
      if (item.acao === 'operacao') this.operacaoAberta = true;
    } else if (item.rota && !item.externa) {
      this.router.navigateByUrl(item.rota).then(ok => {
        if (!ok && this.router.url === item.rota) this.erroNavegacao = 'Você já está nessa tela.';
      }).catch(() => this.erroNavegacao = 'Não foi possível abrir esta função. Tente novamente.');
    } else if (item.externa && item.rota) {
      const url = new URL(item.rota, window.location.origin);
      if (url.protocol === 'https:' || url.protocol === 'http:') window.open(url.href, '_blank', 'noopener,noreferrer');
    }
  }

  private fecharPaineis(fecharMobile = true): void {
    this.pendenciasAbertas = false;
    this.grupoAberto = null;
    this.operacaoAberta = false;
    this.contaAberta = false;
    if (fecharMobile) this.mobileAberto = false;
  }

  fecharComFoco(): void {
    this.fecharPaineis();
    if (this.focoPainel?.getClientRects().length) this.focoPainel.focus();
    else Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(
      '.mv2-mobile-toggle, .mv2-nav-item.aberto, .mv2-operacao-trigger'
    )).find(botao => botao.getClientRects().length)?.focus();
  }

  @HostListener('document:click', ['$event']) cliqueFora(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.fecharPaineis();
  }

  /** Ctrl K e o Escape da busca são tratados pelo próprio diálogo (`app-menu-busca`). */
  @HostListener('document:keydown', ['$event']) atalhoTeclado(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !this.buscaFuncoes?.aberta) {
      if (this.grupoAberto || this.contaAberta || this.operacaoAberta || this.pendenciasAbertas || this.mobileAberto) {
        event.preventDefault();
        this.fecharComFoco();
      }
    }
  }
}
